<template>
  <ToggleSwitch
    class="bz-toggle"
    :input-id="inputId"
    :aria-labelledby="labelledBy"
    :pt="describedBy ? { input: { 'aria-describedby': describedBy } } : undefined"
    :model-value="modelValue"
    @update:model-value="(value) => $emit('update:modelValue', value)"
  />
</template>

<script>
import ToggleSwitch from 'primevue/toggleswitch';

/**
 * A switch, the one both systems share: a filled pill, grey off and the action blue on, with a white
 * thumb that slides. Its colours and sizes are the preset's (src/ui/preset.js).
 *
 * @rule   `inputId` is required, and the row the switch sits in is a `<label for>` it: that makes the
 *         whole row the target (norm 9: ≥ 48 px).
 * @rule   `labelledBy`/`describedBy`: the ids of the row's label and hint (BzListRow's). The switch is
 *         named by the label alone and described by the hint.
 * @why    The state is the thumb's place and the track's colour, never an icon or a size: the white
 *         thumb stands at 10.78:1 on the grey and 5.01:1 on the blue, so either way it is what shows.
 * @why    The thumb slides with a transform, which the compositor moves; PrimeVue moves it with
 *         `inset-inline-start`, a layout change on every frame. Its distance is the preset's own
 *         arithmetic (the track, less the thumb and its two gaps), read from PrimeVue's variables. Left to
 *         right only, as the app's three languages are.
 */
export default {
  name: 'BzToggle',
  components: { ToggleSwitch },
  props: {
    modelValue: { type: Boolean, default: false },
    inputId: { type: String, required: true },
    labelledBy: { type: String, default: undefined },
    describedBy: { type: String, default: undefined },
  },
  emits: ['update:modelValue'],
};
</script>

<style scoped>
.bz-toggle {
  flex-shrink: 0;
}

.bz-toggle :deep(.p-toggleswitch-handle) {
  transition: transform var(--bz-duration-fast) var(--bz-ease-out);
}

.bz-toggle.p-toggleswitch-checked :deep(.p-toggleswitch-handle) {
  inset-inline-start: var(--p-toggleswitch-gap);
  transform: translateX(calc(var(--p-toggleswitch-width) - var(--p-toggleswitch-handle-size) - 2 * var(--p-toggleswitch-gap)));
}

@media (prefers-reduced-motion: reduce) {
  .bz-toggle :deep(.p-toggleswitch-handle) {
    transition: none;
  }
}
</style>
